import {
  closeUnsavedChangesModal,
  enableLoadingStateUnsavedChangesModal,
  openUnsavedChangesModal,
  useTemplate,
} from '@/stores/template';
import { toast } from '@skand/ui';
import { isEqual } from 'lodash-es';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

interface UseAnnotationTemplateUnsavedChangesProps {
  handleSave: () => Promise<void>;
}

export const useAnnotationTemplateUnsavedChanges = ({
  handleSave,
}: UseAnnotationTemplateUnsavedChangesProps): {
  ref: RefObject<HTMLDivElement>;
  isDirty: boolean;
} => {
  const mode = useTemplate(state => state.mode);
  const template = useTemplate(state => state.template);
  const initialTemplate = useTemplate(state => state.initialTemplate);
  const fields = useTemplate(state => state.fields);
  const initialFields = useTemplate(state => state.initialFields);

  const ref = useRef<HTMLDivElement>(null);

  const isDirty = useMemo(() => {
    if (mode === 'create') return true;
    return !isEqual(template, initialTemplate) || !isEqual(fields, initialFields);
  }, [mode, template, fields, initialTemplate, initialFields]);

  /**
   * @see https://stackoverflow.com/a/779785 - Simple explanation
   * @see https://stackoverflow.com/a/4575011 - Detailed explanation with sample code
   */
  const createClickEvent = useCallback((target: HTMLElement) => {
    setTimeout(() => {
      target.dispatchEvent(
        new MouseEvent('click', { bubbles: true, cancelable: true, view: window }),
      );
    }, 0);
  }, []);

  useEffect(() => {
    if (!isDirty || mode === 'create') return;

    const handleGlobalClickEvent = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        ref.current?.contains(target) ||
        !(
          target.tagName === 'BUTTON' ||
          target.tagName === 'A' ||
          target.closest('.cursor-pointer')
        )
      ) {
        return;
      }

      target.tagName === 'A' ? event.preventDefault() : event.stopPropagation();

      openUnsavedChangesModal({
        saveChangesAndProceed: () => {
          enableLoadingStateUnsavedChangesModal();
          handleSave()
            .then(() => {
              useTemplate.setState({ initialTemplate: template, initialFields: fields });
              createClickEvent(target);
            })
            .catch(error =>
              toast({
                type: 'warning',
                message: `Error: ${(error as Error).message}`,
                lifespan: 5000,
                clickToDismiss: true,
              }),
            )
            .finally(() => closeUnsavedChangesModal());
        },
        discardChanges: () => {
          useTemplate.setState({ template: initialTemplate, fields: initialFields });
          createClickEvent(target);
          closeUnsavedChangesModal();
        },
      });
    };

    document.addEventListener('click', handleGlobalClickEvent, true);
    return () => document.removeEventListener('click', handleGlobalClickEvent, true);
  }, [
    createClickEvent,
    fields,
    handleSave,
    initialFields,
    initialTemplate,
    isDirty,
    mode,
    template,
  ]);

  return { ref, isDirty };
};
